import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useHasPermissionSync } from "@/hooks/usePermissions";

export type GlobalSearchResult = {
  type: "pet" | "owner";
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  route: string;
  image?: string | null;
};

const joinMeta = (values: (string | null | undefined)[]) => {
  const parts = values.filter((value): value is string => !!value && value.trim().length > 0);
  return parts.length ? parts.join(" · ") : undefined;
};

type OwnersWithPetsRpcRow = {
  owner_id: string;
  owner_name: string;
  owner_created_at: string;
  owner_document: string | null;
  owner_phones: { phone_number: string }[] | null;
  owner_emails: { email: string }[] | null;
  pet_id: string | null;
  pet_name: string | null;
  pet_record_number: string | null;
  pet_birth_date: string | null;
  pet_created_at: string | null;
  pet_photo: { file_path: string } | null;
  pet_breed: { name: string } | null;
  pet_species: { name: string } | null;
  pet_status: { name: string } | null;
  pet_death_date: string | null;
  pet_count: number;
  total_count: number;
};

export function useGlobalSearch(term: string) {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { hasPermission: canViewPatients, isLoading: isLoadingPermission } = useHasPermissionSync("patients", "view");
  const trimmed = term.trim();

  return useQuery<GlobalSearchResult[]>({
    queryKey: ["global-search", customerId, term],
    enabled: trimmed.length >= 1 && canViewPatients && !isLoadingPermission && !!customerId,
    staleTime: 1000 * 30,
    queryFn: async () => {
      const searchTerm = trimmed;
      if (!customerId) return [];

      // Unify search semantics with the Patients page by reusing the same RPC.
      // This ensures both searches cover: owner/pet names, pet record number,
      // phone, email, document, and address (CEP/street/number).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: searchTerm,
        p_offset: 0,
        p_limit: 60,
      });

      if (error) throw error;

      const rows = (data as OwnersWithPetsRpcRow[] | null | undefined) ?? [];

      const petMap = new Map<string, GlobalSearchResult>();
      const ownerMap = new Map<string, GlobalSearchResult>();

      for (const row of rows) {
        const primaryPhone = row.owner_phones?.[0]?.phone_number ?? undefined;
        const primaryEmail = row.owner_emails?.[0]?.email ?? undefined;

        if (!ownerMap.has(row.owner_id)) {
          ownerMap.set(row.owner_id, {
            type: "owner",
            id: row.owner_id,
            title: row.owner_name,
            subtitle: row.owner_document ?? undefined,
            meta: joinMeta([primaryPhone, primaryEmail]),
            route: `/patients/owners/${row.owner_id}`,
          });
        }

        if (row.pet_id && row.pet_name && !petMap.has(row.pet_id)) {
          petMap.set(row.pet_id, {
            type: "pet",
            id: row.pet_id,
            title: row.pet_name,
            subtitle: row.owner_name,
            meta: joinMeta([
              row.pet_status?.name,
              row.pet_record_number ? `Ficha: ${row.pet_record_number}` : undefined,
              row.owner_document ?? undefined,
              primaryPhone,
              primaryEmail,
            ]),
            route: `/patients/pets/${row.pet_id}`,
            image: row.pet_photo?.file_path ?? null,
          });
        }
      }

      const limitedPetResults = Array.from(petMap.values()).slice(0, 6);
      const limitedOwnerResults = Array.from(ownerMap.values()).slice(0, 6);

      return [...limitedPetResults, ...limitedOwnerResults];
    },
  });
}
